(() => {
if (customElements.get("expanding-gallery")) return;
class ExpandingGallery extends HTMLElement {
  constructor() {
    super();
    this.panels = [];
    this.autoRotate = false;
    this.rotateInterval = null;
    this.rotateIntervalTime = 5000;

    this.handleBlockSelect = (event) => {
      const panel = event.target.closest('.gallery-panel');
      if (panel && this.contains(panel)) { this.activatePanel(panel); this.stopAutoRotate(); }
    };
    this.handleResize = () => {
      this.stopAutoRotate();
      if (this.autoRotate) this.startAutoRotate();
      const panel = this.querySelector('.gallery-panel.active');
      if (panel) this.activatePanel(panel, false);
    };
    this.handleInteraction = () => this.stopAutoRotate();
    // Bind methods
    this.handlePanelClick = this.handlePanelClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  static get observedAttributes() {
    return ["auto-rotate", "rotate-interval"];
  }

  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll(".gallery-panel"));
    this.setupEventListeners();

    // Config
    if (this.hasAttribute("auto-rotate")) {
      this.autoRotate = this.getAttribute("auto-rotate") === "true";
    }

    if (this.hasAttribute("rotate-interval")) {
      this.rotateIntervalTime = parseInt(this.getAttribute("rotate-interval")) || 5000;
    }

    // Auto activate first panel
    if (this.panels.length > 0 && !this.querySelector(".gallery-panel.active")) {
      this.activatePanel(this.panels[0]);
    }

    this.activatePanel(this.querySelector(".gallery-panel.active") || this.panels[0]);
    if (this.autoRotate) {
      this.startAutoRotate();
    }

    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('resize', this.handleResize);
    this.addEventListener('pointerdown', this.handleInteraction);
    this.addEventListener('focusin', this.handleInteraction);

  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('pointerdown', this.handleInteraction);
    this.removeEventListener('focusin', this.handleInteraction);
    this.removeEventListeners();
    this.stopAutoRotate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "auto-rotate") {
      this.autoRotate = newValue === "true";
      this.autoRotate ? this.startAutoRotate() : this.stopAutoRotate();
    }

    if (name === "rotate-interval") {
      this.rotateIntervalTime = parseInt(newValue) || 5000;
      if (this.autoRotate) {
        this.stopAutoRotate();
        this.startAutoRotate();
      }
    }
  }

  setupEventListeners() {
    this.panels.forEach((panel) => {
      panel.addEventListener("click", this.handlePanelClick);
      if (!panel.hasAttribute('tabindex')) panel.tabIndex = 0;
    });

    this.addEventListener("keydown", this.handleKeyDown);
  }

  removeEventListeners() {
    this.panels.forEach((panel) => {
      panel.removeEventListener("click", this.handlePanelClick);
    });

    this.removeEventListener("keydown", this.handleKeyDown);
  }

  handlePanelClick(event) {
    const clickedPanel = event.currentTarget;
    this.activatePanel(clickedPanel);
    this.stopAutoRotate();

    this.dispatchEvent(
      new CustomEvent("panel-changed", {
        detail: {
          panel: clickedPanel,
          index: this.panels.indexOf(clickedPanel),
        },
      }),
    );
  }

  activatePanel(panel, animate = true) {
    if (!panel) return;
    this.panels.forEach((p) => p.classList.remove("active"));
    panel.classList.add("active");
    if (window.matchMedia('(max-width: 1024px)').matches) {
      const bounds = this.getBoundingClientRect();
      const rect = panel.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      this.scrollBy({ left: rtl ? rect.right - bounds.right : rect.left - bounds.left,
        behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
    }
    this.panels.forEach(p => {
      p.querySelectorAll('a, button').forEach(link => {
        if (!link.hasAttribute('data-gallery-tabindex')) link.setAttribute('data-gallery-tabindex', link.getAttribute('tabindex') ?? '');
        if (p !== panel) link.setAttribute('tabindex', '-1');
        else {
          const original = link.getAttribute('data-gallery-tabindex');
          if (original === '') link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', original);
        }
      });
    });

  }

  handleKeyDown(event) {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.classList.contains('gallery-panel')) {
      event.preventDefault();
      this.activatePanel(event.target);
      this.stopAutoRotate();
      return;
    }
    if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const activePanel = this.querySelector(".gallery-panel.active");
    if (!activePanel) return;

    const activeIndex = this.panels.indexOf(activePanel);
    let newIndex;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      newIndex = (activeIndex + 1) % this.panels.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      newIndex = (activeIndex - 1 + this.panels.length) % this.panels.length;
    }

    if (newIndex !== undefined) {
      this.stopAutoRotate();
      this.activatePanel(this.panels[newIndex]);
      this.panels[newIndex].focus({preventScroll: true});
    }
  }

  startAutoRotate() {
    if (this.rotateInterval || !this.isConnected || this.panels.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (matchMedia("(max-width: 1024px)").matches || this.rotateInterval || !this.isConnected || this.panels.length < 2 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.rotateInterval = setInterval(() => {
      const activePanel = this.querySelector(".gallery-panel.active");
      const activeIndex = this.panels.indexOf(activePanel);
      const nextIndex = (activeIndex + 1) % this.panels.length;
      this.activatePanel(this.panels[nextIndex]);
    }, this.rotateIntervalTime);
  }

  stopAutoRotate() {
    if (this.rotateInterval) {
      clearInterval(this.rotateInterval);
      this.rotateInterval = null;
    }
  }

  // Public API
  goToPanel(index) {
    if (index >= 0 && index < this.panels.length) {
      this.activatePanel(this.panels[index]);
    }
  }

  getCurrentIndex() {
    const activePanel = this.querySelector(".gallery-panel.active");
    return this.panels.indexOf(activePanel);
  }
}

customElements.define("expanding-gallery", ExpandingGallery);

})();